/**
 * 25 Oct 2026 (D-1025-EVENTS, proposal §3.8 and Events phase 2): New event asks what it is first.
 *
 *   In-house       in the building; everyone who says yes has a place (No limit starts on)
 *   Outing         off-site; a number of places; Transportation is asked; wheelchair spots count
 *   Raffle         the places are given by a draw
 *   Calendar only  on the calendar, nobody answers
 *
 * The answer is kept in the event's own file (`_app/events/<EventID>.json`, `cal.eventKind`) — no SharePoint column.
 * Only the pair In-house / Outing is ever *read from* the file: whether an event is a raffle or on the calendar only is
 * a fact of the event (its spot rule, its RSVP switch), so the file can never disagree with the event. An event made
 * before today has nothing in the file and gets the kind its facts give (`deriveEventKind`).
 *
 * The word on screen is "kind of event". The older Bingo / Music label (Settings list) is "Category" on screen now, so
 * there are not two "kinds" (proposal risk note, 17 Oct).
 */
import type { SpotRule } from './events';

export const EVENT_KINDS_1025 = ['inhouse', 'outing', 'raffle', 'calendar'] as const;
export type EventKind1025 = (typeof EVENT_KINDS_1025)[number];
export const isEventKind1025 = (v: unknown): v is EventKind1025 => (EVENT_KINDS_1025 as readonly unknown[]).includes(v);

/** What the event's facts say it is. */
export interface KindFacts {
  rule: SpotRule;
  offSite: boolean;
  /** Made from Volunteers → Schedule an outing. */
  outing?: boolean;
  cal: { rsvp: boolean; eventKind?: EventKind1025 | null };
}

/** An event with nothing stored: calendar-only → Calendar only; a raffle → Raffle; off-site or a scheduled outing → Outing; else In-house. */
export function deriveEventKind(f: Pick<KindFacts, 'rule' | 'offSite' | 'outing'> & { cal: { rsvp: boolean } }): EventKind1025 {
  if (!f.cal.rsvp) return 'calendar';
  if (f.rule === 'raffle') return 'raffle';
  if (f.outing || f.offSite) return 'outing';
  return 'inhouse';
}

/**
 * The kind of an event as the screens say it. The file's answer decides only between In-house and Outing; a raffle or a
 * calendar-only event is what its facts say, so turning the raffle or RSVP switch later never leaves a wrong label.
 */
export function eventKindOf(f: KindFacts): EventKind1025 {
  const facts = deriveEventKind(f);
  if (facts !== 'inhouse' && facts !== 'outing') return facts;
  if (f.outing) return 'outing';
  const stored = f.cal.eventKind;
  return stored === 'inhouse' || stored === 'outing' ? stored : facts;
}

/** What New event starts with for each kind (the office can change any of it in Edit). */
export interface KindStart {
  kind: EventKind1025;
  /** People answer (RSVP) or the calendar only. */
  answer: 'rsvp' | 'calendar';
  rule: 'everyone' | 'raffle';
  /** null = the sheet's own default (in the building). */
  offSite: boolean | null;
  /** Starts on "No limit — everyone who says yes has a place". An outing and a raffle start on a number. */
  noLimit: boolean;
  /** Transportation is asked of the people who answer. */
  asksTransport: boolean;
  /** Wheelchair spots are offered (an outing, and a raffle that has them). */
  offersWheelchair: boolean;
}

export function kindStart(kind: EventKind1025): KindStart {
  switch (kind) {
    case 'inhouse':
      return { kind, answer: 'rsvp', rule: 'everyone', offSite: false, noLimit: true, asksTransport: false, offersWheelchair: false };
    case 'outing':
      return { kind, answer: 'rsvp', rule: 'everyone', offSite: true, noLimit: false, asksTransport: true, offersWheelchair: true };
    case 'raffle':
      return { kind, answer: 'rsvp', rule: 'raffle', offSite: null, noLimit: false, asksTransport: false, offersWheelchair: true };
    case 'calendar':
      return { kind, answer: 'calendar', rule: 'everyone', offSite: null, noLimit: false, asksTransport: false, offersWheelchair: false };
  }
}

/** Transportation ("how will you get there") is asked only where there is a "there": an event off-site, or an outing. */
export const asksTransportation = (f: { offSite: boolean; outing?: boolean }): boolean => f.offSite || f.outing === true;
